import React from 'react';
import type { CustomerInfo } from '../../../services/orderLookups.service';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';

interface CustomerInfoModalProps {
  info: CustomerInfo | null;
  onClose: () => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between gap-3">
    <span className="text-muted whitespace-nowrap">{label}</span>
    <span className="text-heading text-right">{value || '-'}</span>
  </div>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1 rounded mb-1.5">
    {children}
  </div>
);

/**
 * Read-only Customer details popup for the Pro drawer's Addl Info tab — the
 * modern take on V1's viewcustomer window. Mirrors LocationInfoModal's
 * structure and styling.
 */
const CustomerInfoModal: React.FC<CustomerInfoModalProps> = ({ info, onClose }) => {
  // Read-only customer card: Alt+C closes. Must sit ABOVE the `!info` guard —
  // this modal stays mounted with info=null, so a hook below the guard would
  // change the hook count when info arrives (React #310). `layer: !!info`
  // is what makes it inert while closed.
  useAltHotkeys({ C: onClose }, undefined, { layer: !!info });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(!!info);

  if (!info) return null;

  return (
    <>
      <div className="fixed inset-0 bg-scrim/30 z-50" onClick={onClose} aria-label="Close customer info" />
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div
        role="dialog"
        aria-label={`Customer details for ${info.name}`}
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto w-[400px] max-h-[80vh] overflow-y-auto bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <ModalHeader
          title={info.name || 'Customer'}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
        />

        <div className="p-4 text-xs space-y-4">
          {/* Record / address header block — V1 viewcustomer parity. */}
          <div className="space-y-1">
            <Row label="Record:" value={String(info.recno)} />
            <Row label="Address:" value={[info.address, info.address2].filter(Boolean).join(', ')} />
            <Row label="City/State/Zip:" value={[info.city, info.state, info.zip].filter(Boolean).join(', ')} />
            <Row label="Phone:" value={info.phone} />
          </div>

          {/* Contacts */}
          <div>
            <SectionHeader>Contacts</SectionHeader>
            {info.contacts.length === 0 ? (
              <div className="text-heading pl-2">-</div>
            ) : (
              <div className="space-y-1 pl-2">
                {info.contacts.map((ct, i) => (
                  <div key={i} className="text-heading">
                    {ct.type && (
                      <span className="text-muted">{ct.type}: </span>
                    )}
                    {[ct.name, ct.phone, ct.email].filter(Boolean).join(' · ') || '-'}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sales */}
          <div>
            <SectionHeader>Sales</SectionHeader>
            <div className="space-y-1">
              <Row label="Sales Rep:" value={info.salesrep} />
              <Row label="Market:" value={info.market} />
            </div>
          </div>

          {info.additionalinfo && (
            <div>
              <div className="text-muted mb-0.5">Additional Info:</div>
              <div className="text-heading whitespace-pre-wrap pl-2">
                {info.additionalinfo}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
};

export default CustomerInfoModal;
